import type {Dispatch, ThunkAction} from 'src/reducers';
import * as reduxApi from 'src/utils/redux-api-v2';
import {key, fetching} from 'src/utils/redux';
import {thunkify as flow} from 'src/utils/thunks';
import urlParse from 'url-parse';
import {getCsrfToken} from 'src/action-creators/csrf';
import {getValidNextPath} from 'src/utils/security';
import {AnalyticsService} from 'src/analytics';
import {createElement} from 'src/utils/dom';
import logger from 'src/utils/logger';

import type {
  CredsAuthentication,
  ApiErrorWithStatusCode,
} from 'src/types/authentication';

import type {Agency} from 'src/types/agency';


export const CREDS_AUTHENTICATION = 'authentication/creds_authentication';
export const CREDS_AUTHENTICATION_ERROR =
  'authentication/creds_authentication_error';
export const CREDS_AUTHENTICATED = 'authentication/creds_authenticated';

export const VALID_AGENCIES = 'authentication/valid_agencies';
export const VALID_AGENCIES_ERROR = 'authentication/valid_agencies_error';

export const NEW_PHONE_REGISTRATION = 'authentication/new_phone_registration';
export const NEW_PHONE_REGISTRATION_ERROR =
  'authentication/new_phone_registration_error';

export const OTP_VERIFICATION_ERROR = 'authentication/otp_verification_error';

export const RESEND_OTP_ERROR = 'authentication/resend_otp_error';

export const RESET_MFA_DEVICE_ERROR = 'authentication/reset_phone_error';

export const RESET_PHONE_APPROVED = 'authentication/reset_phone_approved';

export const RESET_PHONE_APPROVE_ERROR =
  'authentication/reset_phone_approve_error';

export const RESET_TOTP_APPROVED = 'authentication/reset_totp_approved';

export const RESET_TOTP_APPROVE_ERROR =
  'authentication/reset_totp_approve_error';

export const CHANNEL_SELECTION_TOTP_ERROR =
  'authentication/channel_selection_totp_error';

export const FORGOT_PASSWORD_ERROR = 'authentication/forgot_password_error';

export const RESET_PASSWORD_ERROR = 'authentication/reset_password_error';

export const SIGNUP_ERROR = 'authentication/signup_error';

export const RETRY_IN_MINS = 'authentication/retry_in_mins';

type AuthenticatingCredAction = {
  type: typeof CREDS_AUTHENTICATION;
};

type CredAuthErrorAction = {
  type: typeof CREDS_AUTHENTICATION_ERROR;
  payload: {
    error: string;
    retryLoginInMins: number;
  };
};

type CredAuthenticatedAction = {
  type: typeof CREDS_AUTHENTICATED;
  payload: CredsAuthentication;
};

type NewPhoneRegitrationAction = {
  type: typeof NEW_PHONE_REGISTRATION;
  payload: string;
};

type OTPVerificationErrorAction = {
  type: typeof OTP_VERIFICATION_ERROR;
  payload: ApiErrorWithStatusCode;
};

type ResendOTPErrorAction = {
  type: typeof RESEND_OTP_ERROR;
  payload: ApiErrorWithStatusCode;
};

type ResetMfaDeviceErrorAction = {
  type: typeof RESET_MFA_DEVICE_ERROR;
  payload: string;
};

type ResetPhoneApproveErrorAction = {
  type: typeof RESET_PHONE_APPROVE_ERROR;
  payload: string;
};

type ResetPhoneApprovedAction = {
  type: typeof RESET_PHONE_APPROVED;
  payload: string;
};

type ResetTotpApproveErrorAction = {
  type: typeof RESET_TOTP_APPROVE_ERROR;
  payload: string;
};

type ResetTotpApprovedAction = {
  type: typeof RESET_TOTP_APPROVED;
  payload: string;
};

type ChannelSelectionTotpErrorAction = {
  type: typeof CHANNEL_SELECTION_TOTP_ERROR;
  payload: string;
};

type NewPhoneRegistrationError = {
  type: typeof NEW_PHONE_REGISTRATION_ERROR;
  payload: string;
};

type ValidAgenciesAction = {
  type: typeof VALID_AGENCIES;
  payload: Array<Agency>;
};

type ValidAgenciesErrorAction = {
  type: typeof VALID_AGENCIES_ERROR;
  payload: string;
};

type ForgotPasswordErrorAction = {
  type: typeof FORGOT_PASSWORD_ERROR;
  payload: string;
};

type ResetPasswordErrorAction = {
  type: typeof RESET_PASSWORD_ERROR;
  payload: string;
};

type SignUpErrorAction = {
  type: typeof SIGNUP_ERROR;
  payload: string;
};

type RetryInMinsAction = {
  type: typeof RETRY_IN_MINS;
  payload: number;
};

export type AuthenticationActions =
  | AuthenticatingCredAction
  | CredAuthErrorAction
  | CredAuthenticatedAction
  | NewPhoneRegitrationAction
  | OTPVerificationErrorAction
  | ResendOTPErrorAction
  | NewPhoneRegistrationError
  | ValidAgenciesAction
  | ValidAgenciesErrorAction
  | ForgotPasswordErrorAction
  | ResetMfaDeviceErrorAction
  | ResetPhoneApproveErrorAction
  | ResetPhoneApprovedAction
  | ResetTotpApproveErrorAction
  | ResetTotpApprovedAction
  | ResetPasswordErrorAction
  | ChannelSelectionTotpErrorAction
  | SignUpErrorAction
  | RetryInMinsAction;

export const authenticatingCred = (): AuthenticatingCredAction => ({
  type: CREDS_AUTHENTICATION,
});

export const credAuthError = (payload: {
  error: string;
  retryLoginInMins: number;
}): CredAuthErrorAction => ({
  type: CREDS_AUTHENTICATION_ERROR,
  payload,
});

export const credsAuthenticated = (
  payload: CredsAuthentication,
): CredAuthenticatedAction => ({
  type: CREDS_AUTHENTICATED,
  payload,
});

export const setRetryInMins = (payload: number): RetryInMinsAction => ({
  type: RETRY_IN_MINS,
  payload,
});

export const newPhoneRegistration = (
  payload: string,
): NewPhoneRegitrationAction => ({
  type: NEW_PHONE_REGISTRATION,
  payload,
});

export const newPhoneRegistrationError = (
  payload: string,
): NewPhoneRegistrationError => ({
  type: NEW_PHONE_REGISTRATION_ERROR,
  payload,
});

export const OTPVerificationError = (
  payload: ApiErrorWithStatusCode,
): OTPVerificationErrorAction => ({
  type: OTP_VERIFICATION_ERROR,
  payload,
});

export const resendOtpError = (
  payload: ApiErrorWithStatusCode,
): ResendOTPErrorAction => ({
  type: RESEND_OTP_ERROR,
  payload,
});

export const resetMfaDeviceError = (
  payload: string,
): ResetMfaDeviceErrorAction => ({
  type: RESET_MFA_DEVICE_ERROR,
  payload,
});

export const resetPhoneApproveError = (
  payload: string,
): ResetPhoneApproveErrorAction => ({
  type: RESET_PHONE_APPROVE_ERROR,
  payload,
});

export const resetPhoneApproved = (
  payload: string,
): ResetPhoneApprovedAction => ({
  type: RESET_PHONE_APPROVED,
  payload,
});

export const resetTotpApproveError = (
  payload: string,
): ResetTotpApproveErrorAction => ({
  type: RESET_TOTP_APPROVE_ERROR,
  payload,
});

export const resetTotpApproved = (
  payload: string,
): ResetTotpApprovedAction => ({
  type: RESET_TOTP_APPROVED,
  payload,
});

export const channelSelectionTotpError = (
  payload: string,
): ChannelSelectionTotpErrorAction => ({
  type: CHANNEL_SELECTION_TOTP_ERROR,
  payload,
});

export const setValidAgencies = (
  payload: Array<Agency>,
): ValidAgenciesAction => ({
  type: VALID_AGENCIES,
  payload,
});

export const validAgenciesError = (
  payload: string,
): ValidAgenciesErrorAction => ({
  type: VALID_AGENCIES_ERROR,
  payload,
});

export const forgotPasswordError = (
  payload: string,
): ForgotPasswordErrorAction => ({
  type: FORGOT_PASSWORD_ERROR,
  payload,
});

export const resetPasswordError = (
  payload: string,
): ResetPasswordErrorAction => ({
  type: RESET_PASSWORD_ERROR,
  payload,
});

export const signUpError = (payload: string): SignUpErrorAction => ({
  type: SIGNUP_ERROR,
  payload,
});

export const authenticateCreds: (
  email: string,
  password: string,
) => ThunkAction<void> = flow(
  key((email, password) => `auth`),
  fetching(),
)((email, password) => (dispatch: Dispatch) => {
  dispatch(authenticatingCred());
  return dispatch(reduxApi.post(`auth`, {email, password})).then(
    (response) => {
      dispatch(credsAuthenticated(response));
      return response;
    },
    (error) => {
      if (error?.response?.status === 300) {
        // Migrated agent, redirection is handled in component
        return error;
      }
      // default error for login
      let apiError = 'Incorrect Email and Password';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(
        credAuthError({
          error: apiError,
          retryLoginInMins: error?.responseBody?.retryLoginInMins || 0,
        }),
      );
    },
  );
});

export const checkRedirection: () => ThunkAction<unknown> =
  () => (dispatch: Dispatch) => {
    return dispatch(reduxApi.post('auth', {})).then(
      (response) => {
        dispatch(credsAuthenticated(response));
        return response;
      },
      (error) => {
        logger.error('error in redirection check', error);
      },
    );
  };

export const centralLoginAuth: (
  email: string,
  password: string,
) => ThunkAction<void> = flow(
  key((email, password) => `auth/valid-agencies`),
  fetching(),
)((email, password) => (dispatch: Dispatch) => {
  dispatch(authenticatingCred());
  return dispatch(reduxApi.post(`auth/valid-agencies`, {email, password})).then(
    (response) => {
      dispatch(setValidAgencies(response));
      return response;
    },
    (error) => {
      // default error for login
      let apiError = 'Incorrect Email and Password';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(validAgenciesError(apiError));
    },
  );
});

export const mockSignIn: (
  email: string,
  password: string,
  domain: string,
  slug: string,
) => ThunkAction<unknown> =
  (email, password, domain, slug) =>
  (dispatch: Dispatch): Promise<boolean> => {
    return new Promise(
      async (resolve: (result: Promise<boolean> | boolean) => void) => {
        const action = getFormAction(slug, domain);
        const fakeForm = createElement(document, 'form', {
          style: 'display: none',
          method: 'POST',
          action,
        });

        const fakeFormEmailInput = createElement(document, 'input', {
          name: 'email',
          value: email,
        });
        fakeForm.appendChild(fakeFormEmailInput);

        const fakeFormPasswordInput = createElement(document, 'input', {
          name: 'password',
          type: 'password',
          value: password,
        });
        fakeForm.appendChild(fakeFormPasswordInput);

        window.document.body.appendChild(fakeForm);

        // @ts-expect-error - TS2551 - Property 'submit' does not exist on type 'HTMLElement'. Did you mean 'onsubmit'?
        fakeForm.submit();

        resolve(true);
      },
    );
  };

const getFormAction = (slug: string, domain: string) => {
  // @ts-expect-error - TS2769 - No overload matches this call.
  const query = urlParse(window.location, true).query;
  const nextPath = query.nextPathname || '';
  const validNextPath = getValidNextPath(nextPath);
  const nextSearch = query.nextSearch || '';
  const origin = `https://${slug}.${domain}`;

  const defaultPath = encodeURIComponent(
    `${origin}${validNextPath || ''}${nextSearch || ''}${
      nextSearch ? '&' : '?'
    }redirect=true`,
  );
  // use redirect=True for checking the auth condition on the next request
  // else we'll have to call the auth request on each call
  const action = `${origin}/api/v1/auth?default_path=${defaultPath}`;
  return action;
};

export const registerNewPhone: (
  phone: string,
  email: string,
  loginId: string,
) => ThunkAction<void> = flow(
  key((phone, email, loginId) => 'mfa/register-new-phone'),
  fetching(),
)((phone, email, loginId) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`mfa/register-new-phone`, {
      phone,
      email,
      login_id: loginId,
    }),
  ).then(
    (response) => {
      dispatch(newPhoneRegistration(phone));
      return response;
    },
    (error) => {
      // default error for resend OTP
      let apiError = 'Error Sending OTP to the phone';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }

      dispatch(newPhoneRegistrationError(apiError));
    },
  );
});

export const setTotpOrSmsAuthForTotpVerification: (
  email: string,
  loginId: string,
  authChannel: string,
) => ThunkAction<void> = flow(
  key(() => 'mfa/verify'),
  fetching(),
)((email, loginId, authChannel) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`mfa/send`, {
      email,
      login_id: loginId,
      auth_channel: authChannel,
    }),
  ).then(
    (response) => {
      return response;
    },
    (error) => {
      // default error for login
      let apiError = 'Something went wrong';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(channelSelectionTotpError(apiError));
    },
  );
});

export const verifyOtpForUser: (
  otp: string,
  email: string,
  loginId: string,
  newPhone: string,
  authChannel: string,
  authenticate?: boolean,
) => ThunkAction<void> = flow(
  key((otp, email, loginId, newPhone) => 'mfa/verify'),
  fetching(),
)(
  (otp, email, loginId, newPhone, authChannel, authenticate = true) =>
    (dispatch: Dispatch) => {
      return dispatch(
        reduxApi.post(`mfa/verify`, {
          user_otp: otp,
          email,
          login_id: loginId,
          new_phone: newPhone,
          auth_channel: authChannel,
          authenticate,
        }),
      ).then(
        (response) => {
          return response;
        },
        (error) => {
          // default error for login
          let apiError = 'Incorrect OTP Entered';
          if (error?.responseBody?.errors && error.responseBody.errors[0]) {
            apiError = error.responseBody.errors[0];
          }
          dispatch(
            OTPVerificationError({
              error: apiError,
              status_code: error.response.status,
              error_type: error?.responseBody?.error_type,
              pending_attempts: error?.responseBody?.pending_attempts,
            }),
          );
        },
      );
    },
);

export const performSignIn: (agency: Agency) => ThunkAction<unknown> =
  (agency) =>
  (dispatch: Dispatch): Promise<boolean> => {
    return new Promise(
      async (resolve: (result: Promise<boolean> | boolean) => void) => {
        try {
          await dispatch(getCsrfToken());
          const {host, protocol} = window.location;
          const baseDomain = host.substring(host.indexOf('.') + 1);
          // @ts-expect-error - TS2769 - No overload matches this call.
          const query = urlParse(window.location, true).query;
          const nextPath = query.nextPathname || '';
          const nextSearch = query.nextSearch || '';
          trackLogin('sign-in', {isSuccees: true});
          const validNextPath = getValidNextPath(nextPath);
          // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'string & Location'.
          window.location = `${protocol}//${agency.slug}.${baseDomain}${validNextPath}${nextSearch}`;
          resolve(true);
        } catch (error: any) {
          trackLogin('sign-in', {
            isSuccees: false,
            error: JSON.stringify(error),
          });
          logger.error(error);
        }
      },
    );
  };

function trackLogin(
  event: string,
  props:
    | {
        error: string;
        isSuccees: boolean;
      }
    | {
        isSuccees: boolean;
      },
) {
  try {
    AnalyticsService.track(event, props);
  } catch (error: any) {
    console.log('event tracking error ' + error);
  }
}

export const resendOtp: (
  email: string,
  loginId: string,
  newPhone: string,
) => ThunkAction<void> = flow(
  key((email, loginId, newPhone) => 'mfa/resend'),
  fetching(),
)((email, loginId, newPhone) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`mfa/resend`, {
      login_id: loginId,
      email,
      new_phone: newPhone,
    }),
  ).then(
    (response) => {
      return response;
    },
    (error) => {
      // default error for resend OTP
      let apiError = 'Error Sending OTP to the phone';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(
        resendOtpError({error: apiError, status_code: error.response.status}),
      );
    },
  );
});

export const resetPhone: (email: string) => ThunkAction<void> = flow(
  key((email) => 'mfa-setting/phone/reset'),
  fetching(),
)((email) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`mfa-setting/phone/reset`, {
      email,
    }),
  ).then(
    (response) => {
      return response;
    },
    (error) => {
      // default error for reset OTP
      let apiError = 'Error while removing the phone number';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(resetMfaDeviceError(apiError));
    },
  );
});

export const resetPhoneApprove: (approval_token: string) => ThunkAction<void> =
  flow(
    key((approval_token) => 'mfa-setting/phone/reset/approve'),
    fetching(),
  )((approval_token) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(`mfa-setting/phone/reset/approve`, {
        approval_token,
      }),
    ).then(
      (response) => {
        const userEmail = response.user_email;
        dispatch(resetPhoneApproved(userEmail));
        return userEmail;
      },
      (error) => {
        // default error for approving reset phone
        let apiError = 'Error while approving the reset phone number request';
        if (error?.responseBody?.errors && error.responseBody.errors[0]) {
          apiError = error.responseBody.errors[0];
        } else if (error?.responseBody?.error === 'Unauthorized') {
          apiError = 'Log in first to confirm MFA reset';
        }
        dispatch(resetPhoneApproveError(apiError));
      },
    );
  });

export const resetTotpApprove: (approval_token: string) => ThunkAction<void> =
  flow(
    key(() => 'mfa-setting/mfa/reset/approve'),
    fetching(),
  )((approval_token) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(`mfa-setting/mfa/reset/approve`, {
        approval_token,
      }),
    ).then(
      (response) => {
        const userEmail = response.user_email;
        dispatch(resetTotpApproved(userEmail));
        return userEmail;
      },
      (error) => {
        // default error for approving reset authenticator app
        let apiError =
          'Error while approving the reset authenticator app request';
        if (error?.responseBody?.errors && error.responseBody.errors[0]) {
          apiError = error.responseBody.errors[0];
        } else if (error?.responseBody?.error === 'Unauthorized') {
          apiError = 'Log in first to confirm MFA reset';
        }
        dispatch(resetTotpApproveError(apiError));
      },
    );
  });

export const forgotPassword: (email: string) => ThunkAction<void> = flow(
  key((email) => 'accounts/me/forgot-password'),
  fetching(),
)((email) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`accounts/me/forgot-password`, {
      email,
    }),
  ).then(
    (response) => {
      return response;
    },
    (error) => {
      // default error for forgot password
      let apiError = 'Failed to perform action, please try after sometime';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(forgotPasswordError(apiError));
    },
  );
});

export const forgotPasswordNoAgency: (email: string) => ThunkAction<void> =
  flow(
    key((email) => 'accounts/me/forgot-password-no-agency'),
    fetching(),
  )((email) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(`accounts/me/forgot-password-no-agency`, {
        email,
      }),
    ).then(
      (response) => {
        return response;
      },
      (error) => {
        // default error for forgot password
        let apiError = 'Failed to perform action, please try after sometime';
        if (error?.responseBody?.errors && error.responseBody.errors[0]) {
          apiError = error.responseBody.errors[0];
        }
        dispatch(forgotPasswordError(apiError));
      },
    );
  });

// @ts-expect-error - TS2322 - Type '(reset_id: string, new_password: string, new_password_copy: string) => (dispatch: Dispatch) => Promise<boolean | void>' is not assignable to type '(reset_id: string, new_password: string, new_password_copy: string) => ThunkAction<void>'.
export const resetPassword: (
  reset_id: string,
  new_password: string,
  new_password_copy: string,
) => ThunkAction<void> = flow(
  key(
    (reset_id, new_password, new_password_copy) => 'accounts/me/reset-password',
  ),
  fetching(),
)((reset_id, new_password, new_password_copy) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`accounts/me/reset-password`, {
      reset_id,
      new_password,
      new_password_copy,
    }),
  ).then(
    (response) => {
      return true;
    },
    (error) => {
      // default error for forgot password
      let apiError = 'Failed to perform action, please try after sometime';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(resetPasswordError(apiError));
    },
  );
});

// @ts-expect-error - TS2322 - Type '(password: string, new_password: string, new_password_copy: string) => (dispatch: Dispatch) => Promise<boolean | void>' is not assignable to type '(password: string, new_password: string, new_password_copy: string) => ThunkAction<void>'.
export const updatePassword: (
  password: string,
  new_password: string,
  new_password_copy: string,
) => ThunkAction<void> = flow(
  key((password, new_password, new_password_copy) => 'accounts/me/password'),
  fetching(),
)((password, new_password, new_password_copy) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`accounts/me/password`, {
      password,
      new_password,
      new_password_copy,
    }),
  ).then(
    (response) => {
      return true;
    },
    (error) => {
      // default error for reset password
      let apiError = 'Failed to perform action, please try after sometime';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(resetPasswordError(apiError));
    },
  );
});

export const signUpUser: (
  fullname: string,
  email: string,
  password: string,
  invite_code?: string | null | undefined,
) => ThunkAction<void> = flow(
  key((fullname, email, password, invite_code) => 'accounts'),
  fetching(),
)((fullname, email, password, invite_code) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`accounts`, {
      fullname,
      email,
      password,
      invite_code,
    }),
  ).then(
    (response) => {
      dispatch(credsAuthenticated(response));
      return response;
    },
    (error) => {
      // default error for reset password
      let apiError = 'Failed to perform action, please try after sometime';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(signUpError(apiError));
    },
  );
});

export const resetMfaDevice: (email: string) => ThunkAction<void> = flow(
  key(() => 'mfa-setting/mfa/reset'),
  fetching(),
)((email) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`mfa-setting/mfa/reset`, {
      email,
    }),
  ).then(
    (response) => {
      dispatch(resetMfaDeviceError(''));
      return response;
    },
    (error) => {
      // default error for reset MFA device
      let apiError = 'Error while removing the MFA device';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch(resetMfaDeviceError(apiError));
    },
  );
});
